import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { onValue, push, ref, set } from 'firebase/database';
import { Ban, Loader2, Plus, Trash2, UserCheck, UserMinus } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import ActionModal from '../components/ActionModal';
import { auth, database } from '../services/firebase';
import {
  manageOrganizationUser,
  type ManageOrganizationUserAction,
} from '../services/organizationUsers';

type UserProfile = {
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
};

type OrganizationUserRecord = {
  userId: string;
  organizationId: string;
  status: string;
  role: string;
  displayName?: string | null;
  email?: string | null;
  joinedAt?: number;
  requestedAt?: number;
  approvedAt?: number;
  deactivatedAt?: number;
  updatedAt?: number;
};

type AddUserFormState = {
  firstName: string;
  lastName: string;
  email: string;
  role: 'Administrator' | 'Member';
  password: string;
};

type StatusKey = 'active' | 'pending' | 'inactive' | 'unknown';

const addUserRoleOptions: Array<{ value: AddUserFormState['role']; label: string }> = [
  { value: 'Administrator', label: 'Administrator' },
  { value: 'Member', label: 'Member' },
];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialAddUserFormState: AddUserFormState = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'Member',
  password: '',
};

function normalizeStatus(status?: string | null): StatusKey {
  if (!status) {
    return 'unknown';
  }
  const normalized = status.toString().trim().toLowerCase();
  if (normalized === 'active' || normalized === 'pending' || normalized === 'inactive') {
    return normalized;
  }
  return 'unknown';
}

function formatStatusLabel(status: StatusKey): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'pending':
      return 'Pending';
    case 'inactive':
      return 'Inactive';
    default:
      return 'Unknown';
  }
}

function extractErrorMessage(error: unknown, fallbackMessage: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null) {
    const candidate = error as { message?: unknown; details?: unknown; code?: unknown };
    if (typeof candidate.message === 'string' && candidate.message.trim()) {
      return candidate.message;
    }
    if (typeof candidate.details === 'string' && candidate.details.trim()) {
      return candidate.details;
    }
    if (typeof candidate.code === 'string' && candidate.code.trim()) {
      return `${fallbackMessage} (${candidate.code})`;
    }
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return fallbackMessage;
}

export default function Users() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [organizationUsers, setOrganizationUsers] = useState<OrganizationUserRecord[]>([]);
  const [organizationUsersLoading, setOrganizationUsersLoading] = useState(false);
  const [organizationUsersError, setOrganizationUsersError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingActions, setPendingActions] = useState<Record<string, ManageOrganizationUserAction>>({});
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [addUserForm, setAddUserForm] =
    useState<AddUserFormState>(initialAddUserFormState);
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const addUserFirstNameRef = useRef<HTMLInputElement | null>(null);
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/users' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!isAddUserModalOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      addUserFirstNameRef.current?.focus();
    }, 30);
    return () => window.clearTimeout(timer);
  }, [isAddUserModalOpen]);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    const profileRef = ref(database, `users/${currentUser.uid}`);
    setProfileLoading(true);
    const unsubscribe = onValue(
      profileRef,
      snapshot => {
        const value = snapshot.val() as UserProfile | null;
        if (value?.needsOrganizationSetup) {
          setProfileLoading(false);
          navigate('/organization/create', {
            replace: true,
            state: { returnTo: '/users' },
          });
          return;
        }
        setProfile(value);
        setProfileLoading(false);
      },
      error => {
        console.warn('Failed to load users profile data', error);
        setProfile(null);
        setProfileLoading(false);
      },
    );

    return () => unsubscribe();
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!profile?.organizationId) {
      setOrganizationUsers([]);
      setOrganizationUsersLoading(false);
      setOrganizationUsersError(
        !profileLoading && !profile?.needsOrganizationSetup ? 'No organization details were found.' : null,
      );
      return;
    }

    const organizationId = profile.organizationId;
    const orgUsersRef = ref(database, `organizationUsers/${organizationId}`);
    setOrganizationUsersLoading(true);
    setOrganizationUsersError(null);

    const unsubscribe = onValue(
      orgUsersRef,
      snapshot => {
        const value = snapshot.val() as Record<string, Partial<OrganizationUserRecord>> | null;
        if (!value) {
          setOrganizationUsers([]);
          setOrganizationUsersLoading(false);
          return;
        }

        const parsed = Object.entries(value).map(([userId, data]) => {
          const record = data ?? {};
          return {
            userId,
            organizationId: organizationId,
            status: typeof record.status === 'string' ? record.status : 'Unknown',
            role: typeof record.role === 'string' ? record.role : 'Member',
            displayName: typeof record.displayName === 'string' ? record.displayName : null,
            email: typeof record.email === 'string' ? record.email : null,
            joinedAt: typeof record.joinedAt === 'number' ? record.joinedAt : undefined,
            requestedAt: typeof record.requestedAt === 'number' ? record.requestedAt : undefined,
            approvedAt: typeof record.approvedAt === 'number' ? record.approvedAt : undefined,
            deactivatedAt: typeof record.deactivatedAt === 'number' ? record.deactivatedAt : undefined,
            updatedAt: typeof record.updatedAt === 'number' ? record.updatedAt : undefined,
          };
        });

        parsed.sort((a, b) => {
          const statusOrder: Record<StatusKey, number> = { active: 0, pending: 1, inactive: 2, unknown: 3 };
          const aStatus = statusOrder[normalizeStatus(a.status)];
          const bStatus = statusOrder[normalizeStatus(b.status)];
          if (aStatus !== bStatus) {
            return aStatus - bStatus;
          }
          const aTime = a.joinedAt ?? a.requestedAt ?? 0;
          const bTime = b.joinedAt ?? b.requestedAt ?? 0;
          return aTime - bTime;
        });

        setOrganizationUsers(parsed);
        setOrganizationUsersLoading(false);
      },
      error => {
        console.warn('Failed to subscribe to organization users', error);
        const codeCandidate =
          typeof (error as { code?: unknown })?.code === 'string'
            ? String((error as { code?: unknown }).code)
            : null;
        const message =
          codeCandidate === 'PERMISSION_DENIED'
            ? 'You need to be an organization owner to manage team members.'
            : 'We were unable to load team members. Please try again later.';
        setOrganizationUsers([]);
        setOrganizationUsersError(message);
        setOrganizationUsersLoading(false);
      },
    );

    return () => unsubscribe();
  }, [profile?.organizationId, profile?.needsOrganizationSetup, profileLoading]);

  const cardClass = `${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} rounded-lg p-8 ring-1`;
  const mutedTextClass = `${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`;
  const descriptionClass = `text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`;
  const primaryActionButtonClass = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
    isDarkMode ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700'
  }`;
  const secondaryActionButtonClass = `inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
    isDarkMode
      ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700'
      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
  }`;
  const dangerActionButtonClass = `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
    isDarkMode
      ? 'bg-red-500/20 text-red-200 hover:bg-red-500/30'
      : 'bg-red-50 text-red-600 hover:bg-red-100'
  }`;
  const loaderTextClass = isDarkMode ? 'text-slate-200' : 'text-slate-600';
  const badgeBaseClass = `inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold`;
  const badgeColors: Record<StatusKey, string> = {
    active: isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700',
    pending: isDarkMode ? 'bg-amber-500/20 text-amber-200' : 'bg-amber-100 text-amber-700',
    inactive: isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-200 text-slate-700',
    unknown: isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-500',
  };
  const errorBannerClass = `${isDarkMode ? 'border-red-500/40 bg-red-500/10 text-red-200' : 'border-red-300 bg-red-50 text-red-700'} rounded-xl border px-4 py-3 text-sm`;
  const infoBannerClass = `${isDarkMode ? 'border-slate-700 bg-slate-900/40 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-600'} rounded-xl border px-4 py-3 text-sm`;
  const labelClass = `text-sm font-medium ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`;
  const inputClass = `mt-2 w-full rounded-xl border px-4 py-3 text-base shadow-sm transition focus:outline-none focus:ring-2 ${
    isDarkMode
      ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500'
      : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-600 focus:ring-blue-600'
  }`;
  const inputToggleButtonClass = `absolute inset-y-0 right-0 flex items-center px-3 text-sm font-semibold transition ${
    isDarkMode ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'
  }`;

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/users' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/users' } });
  };

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/users' },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const pendingMembers = useMemo(
    () => organizationUsers.filter(member => normalizeStatus(member.status) === 'pending'),
    [organizationUsers],
  );
  const activeMembers = useMemo(
    () => organizationUsers.filter(member => normalizeStatus(member.status) === 'active'),
    [organizationUsers],
  );
  const inactiveMembers = useMemo(
    () => organizationUsers.filter(member => normalizeStatus(member.status) === 'inactive'),
    [organizationUsers],
  );

  const currentMembership = useMemo(() => {
    if (!currentUser) {
      return null;
    }
    return organizationUsers.find(member => member.userId === currentUser.uid) ?? null;
  }, [currentUser, organizationUsers]);

  const canManageMembers =
    Boolean(currentMembership) &&
    normalizeStatus(currentMembership?.status) === 'active' &&
    ['owner', 'administrator'].includes(currentMembership?.role?.toLowerCase() ?? '');

  const resetAddUserForm = () => {
    setAddUserForm(initialAddUserFormState);
    setAddUserError(null);
    setShowAddUserPassword(false);
  };

  const handleOpenAddUserModal = () => {
    if (!canManageMembers || addUserSubmitting) {
      return;
    }
    resetAddUserForm();
    setIsAddUserModalOpen(true);
  };

  const handleCloseAddUserModal = () => {
    if (addUserSubmitting) {
      return;
    }
    setIsAddUserModalOpen(false);
    resetAddUserForm();
  };

  const handleAddUserSave = async () => {
    if (!canManageMembers) {
      setAddUserError('You need to be an organization owner to add members.');
      return;
    }
    if (!profile?.organizationId) {
      setAddUserError('We could not determine your organization. Please refresh and try again.');
      return;
    }
    const trimmedFirstName = addUserForm.firstName.trim();
    const trimmedLastName = addUserForm.lastName.trim();
    const trimmedEmail = addUserForm.email.trim().toLowerCase();
    const trimmedPassword = addUserForm.password.trim();
    if (!trimmedFirstName) {
      setAddUserError('First name is required.');
      return;
    }
    if (!trimmedLastName) {
      setAddUserError('Last name is required.');
      return;
    }
    if (!trimmedEmail) {
      setAddUserError('Email is required.');
      return;
    }
    if (!emailPattern.test(trimmedEmail)) {
      setAddUserError('Please enter a valid email address.');
      return;
    }
    if (trimmedPassword.length < 8) {
      setAddUserError('Password must be at least 8 characters.');
      return;
    }

    setAddUserSubmitting(true);
    setAddUserError(null);

    try {
      const organizationId = profile.organizationId;
      const membersRef = ref(database, `organizationUsers/${organizationId}`);
      const newMemberRef = push(membersRef);
      const newMemberId = newMemberRef.key;

      if (!newMemberId) {
        throw new Error('Unable to create a new member id.');
      }

      const now = Date.now();
      const displayName = `${trimmedFirstName} ${trimmedLastName}`.replace(/\s+/g, ' ').trim();

      await set(newMemberRef, {
        organizationId,
        userId: newMemberId,
        status: 'Active',
        role: addUserForm.role,
        displayName,
        email: trimmedEmail,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        joinedAt: now,
        invitedAt: now,
        invitedBy: currentUser?.uid ?? null,
        needsOrganizationSetup: false,
        orgId: organizationId,
        orgMembershipStatus: 'Active',
        temporaryPassword: trimmedPassword,
        passwordSetAt: now,
        passwordSetBy: currentUser?.uid ?? null,
      });

      setIsAddUserModalOpen(false);
      resetAddUserForm();
    } catch (error) {
      console.warn('Failed to add organization member', error);
      setAddUserError('We were unable to add that user. Please try again.');
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const handleMemberAction = async (
    member: OrganizationUserRecord,
    action: ManageOrganizationUserAction,
  ) => {
    if (!profile?.organizationId) {
      setActionError('We could not determine your organization. Please refresh and try again.');
      return;
    }

    setActionError(null);
    setPendingActions(prev => ({ ...prev, [member.userId]: action }));
    try {
      await manageOrganizationUser({
        organizationId: profile.organizationId,
        userId: member.userId,
        action,
      });
    } catch (error) {
      console.warn('Failed to manage organization member', { action, member: member.userId, error });
      setActionError(
        extractErrorMessage(error, 'We were unable to update that member. Please try again.'),
      );
    } finally {
      setPendingActions(prev => {
        const next = { ...prev };
        delete next[member.userId];
        return next;
      });
    }
  };

  const handleApprove = (member: OrganizationUserRecord) => {
    void handleMemberAction(member, 'approve');
  };

  const handleReject = (member: OrganizationUserRecord) => {
    void handleMemberAction(member, 'reject');
  };

  const handleDeactivate = (member: OrganizationUserRecord) => {
    void handleMemberAction(member, 'deactivate');
  };

  const handleRemove = (member: OrganizationUserRecord) => {
    void handleMemberAction(member, 'remove');
  };

  const hasOrganizationAccess = Boolean(profile?.organizationId);
  const isContentLoading =
    profileLoading || (profile?.organizationId ? organizationUsersLoading : false);
  const trimmedAddUserFirstName = addUserForm.firstName.trim();
  const trimmedAddUserLastName = addUserForm.lastName.trim();
  const trimmedAddUserEmail = addUserForm.email.trim();
  const trimmedAddUserPassword = addUserForm.password.trim();
  const isAddUserFormValid =
    Boolean(
      trimmedAddUserFirstName &&
        trimmedAddUserLastName &&
        emailPattern.test(trimmedAddUserEmail) &&
        trimmedAddUserPassword.length >= 8,
    );
  const isAddUserSaveDisabled = !isAddUserFormValid || addUserSubmitting;

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Loading team tools...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      hasOrganizationAccess={hasOrganizationAccess}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
    >
      {isContentLoading ? (
        <div className="flex justify-center pt-12">
          <div className={`flex items-center gap-3 text-sm font-semibold ${loaderTextClass}`}>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading team members...
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <section className={cardClass}>
            {/* Header Row */}
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold">Manage team members</h1>

              <button
                type="button"
                className={primaryActionButtonClass}
                onClick={handleOpenAddUserModal}
                disabled={!canManageMembers || !hasOrganizationAccess || addUserSubmitting}
                title={
                  !canManageMembers
                    ? 'Only organization owners can add members.'
                    : undefined
                }
              >
                <Plus className="h-5 w-5" aria-hidden="true" />
                Add User
              </button>
            </div>

            {/* Subtitle below both */}
            <p className={`${mutedTextClass} mt-3`}>
              Review pending requests, approve access, or deactivate team members.
            </p>

            {!hasOrganizationAccess ? (
              <div className={`${infoBannerClass} mt-6`}>
                Create or join an organization before managing your team.
              </div>
            ) : (
              <>
                {organizationUsersError ? (
                  <div className={`${errorBannerClass} mt-6`}>{organizationUsersError}</div>
                ) : (
                  <>
                    {actionError && <div className={`${errorBannerClass} mt-6`}>{actionError}</div>}

                    <div className="mt-6 grid gap-4 sm:grid-cols-3">
                      <div className={`${isDarkMode ? 'bg-slate-900/70' : 'bg-slate-50'} rounded-xl p-4`}>
                        <p className={`${mutedTextClass} text-xs uppercase tracking-wide`}>Active</p>
                        <p className="mt-1 text-2xl font-semibold">{activeMembers.length}</p>
                      </div>
                      <div className={`${isDarkMode ? 'bg-slate-900/70' : 'bg-slate-50'} rounded-xl p-4`}>
                        <p className={`${mutedTextClass} text-xs uppercase tracking-wide`}>Pending</p>
                        <p className="mt-1 text-2xl font-semibold">{pendingMembers.length}</p>
                      </div>
                      <div className={`${isDarkMode ? 'bg-slate-900/70' : 'bg-slate-50'} rounded-xl p-4`}>
                        <p className={`${mutedTextClass} text-xs uppercase tracking-wide`}>Inactive</p>
                        <p className="mt-1 text-2xl font-semibold">{inactiveMembers.length}</p>
                      </div>
                    </div>

                    {organizationUsers.length === 0 && (
                      <p className={`${mutedTextClass} mt-6`}>
                        Invite your first teammate to get started.
                      </p>
                    )}

                    {pendingMembers.length > 0 && (
                      <div className="mt-8">
                        <h2 className="text-lg font-semibold">Pending requests</h2>
                        <p className={`${mutedTextClass} text-sm`}>
                          Approve requests to grant access or reject to remove them from the queue.
                        </p>
                        <div className="mt-4 space-y-3">
                          {pendingMembers.map(member => {
                            const normalizedStatus = normalizeStatus(member.status);
                            const isProcessing = Boolean(pendingActions[member.userId]);
                            return (
                              <div
                                key={member.userId}
                                className={`flex flex-col items-start justify-between gap-4 rounded-2xl border px-4 py-4 sm:flex-row sm:items-center ${
                                  isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-white'
                                }`}
                              >
                                <div>
                                  <p className="text-sm font-semibold">
                                    {member.displayName?.trim() || member.email?.trim() || member.userId}
                                  </p>
                                  <p className={`${mutedTextClass} text-xs`}>
                                    {formatStatusLabel(normalizedStatus)} · {member.role || 'Member'}
                                  </p>
                                </div>
                                {canManageMembers && (
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className={primaryActionButtonClass}
                                      onClick={() => handleApprove(member)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing && pendingActions[member.userId] === 'approve' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                      ) : (
                                        <UserCheck className="h-4 w-4" aria-hidden="true" />
                                      )}
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      className={secondaryActionButtonClass}
                                      onClick={() => handleReject(member)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing && pendingActions[member.userId] === 'reject' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                      ) : (
                                        <Ban className="h-4 w-4" aria-hidden="true" />
                                      )}
                                      Reject
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {activeMembers.length > 0 && (
                      <div className="mt-10">
                        <h2 className="text-lg font-semibold">Active members</h2>
                        <p className={`${mutedTextClass} text-sm`}>
                          Deactivate to pause access or remove to revoke membership completely.
                        </p>
                        <div className="mt-4 space-y-3">
                          {activeMembers.map(member => {
                            const normalizedStatus = normalizeStatus(member.status);
                            const badgeClass = `${badgeBaseClass} ${badgeColors[normalizedStatus]}`;
                            const isProcessing = Boolean(pendingActions[member.userId]);
                            const isCurrentUser = member.userId === currentUser.uid;
                            const memberRole = member.role || 'Member';
                            return (
                              <div
                                key={member.userId}
                                className={`flex flex-col items-start justify-between gap-4 rounded-2xl border px-4 py-4 sm:flex-row sm:items-center ${
                                  isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-white'
                                }`}
                              >
                                <div className="space-y-1">
                                  <p className="text-sm font-semibold">
                                    {member.displayName?.trim() || member.email?.trim() || member.userId}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className={badgeClass}>{formatStatusLabel(normalizedStatus)}</span>
                                    <span className={`${mutedTextClass} text-xs uppercase tracking-wide`}>
                                      {memberRole}
                                    </span>
                                  </div>
                                </div>
                                {canManageMembers && !isCurrentUser && member.role?.toLowerCase() !== 'owner' && (
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className={secondaryActionButtonClass}
                                      onClick={() => handleDeactivate(member)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing && pendingActions[member.userId] === 'deactivate' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                      ) : (
                                        <UserMinus className="h-4 w-4" aria-hidden="true" />
                                      )}
                                      Deactivate
                                    </button>
                                    <button
                                      type="button"
                                      className={dangerActionButtonClass}
                                      onClick={() => handleRemove(member)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing && pendingActions[member.userId] === 'remove' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                      ) : (
                                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                                      )}
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {inactiveMembers.length > 0 && (
                      <div className="mt-10">
                        <h2 className="text-lg font-semibold">Inactive members</h2>
                        <p className={`${mutedTextClass} text-sm`}>
                          Reactivate to restore access or remove the user entirely.
                        </p>
                        <div className="mt-4 space-y-3">
                          {inactiveMembers.map(member => {
                            const normalizedStatus = normalizeStatus(member.status);
                            const badgeClass = `${badgeBaseClass} ${badgeColors[normalizedStatus]}`;
                            const isProcessing = Boolean(pendingActions[member.userId]);
                            const isCurrentUser = member.userId === currentUser.uid;
                            return (
                              <div
                                key={member.userId}
                                className={`flex flex-col items-start justify-between gap-4 rounded-2xl border px-4 py-4 sm:flex-row sm:items-center ${
                                  isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-white'
                                }`}
                              >
                                <div className="space-y-1">
                                  <p className="text-sm font-semibold">
                                    {member.displayName?.trim() || member.email?.trim() || member.userId}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className={badgeClass}>{formatStatusLabel(normalizedStatus)}</span>
                                    <span className={`${mutedTextClass} text-xs uppercase tracking-wide`}>
                                      {member.role || 'Member'}
                                    </span>
                                  </div>
                                </div>
                                {canManageMembers && !isCurrentUser && (
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      className={primaryActionButtonClass}
                                      onClick={() => handleApprove(member)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing && pendingActions[member.userId] === 'approve' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                      ) : (
                                        <UserCheck className="h-4 w-4" aria-hidden="true" />
                                      )}
                                      Reactivate
                                    </button>
                                    <button
                                      type="button"
                                      className={dangerActionButtonClass}
                                      onClick={() => handleRemove(member)}
                                      disabled={isProcessing}
                                    >
                                      {isProcessing && pendingActions[member.userId] === 'remove' ? (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                      ) : (
                                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                                      )}
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </section>
        </div>
      )}
      <ActionModal
        isOpen={isAddUserModalOpen}
        title="Add team member"
        onClose={handleCloseAddUserModal}
        onCancel={handleCloseAddUserModal}
        onSave={handleAddUserSave}
        isDarkMode={isDarkMode}
        isSaving={addUserSubmitting}
        disableSave={isAddUserSaveDisabled}
        saveLabel="Add user"
        size="md"
      >
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="add-user-first-name" className={labelClass}>
                First name
              </label>
              <input
                id="add-user-first-name"
                ref={addUserFirstNameRef}
                type="text"
                className={inputClass}
                value={addUserForm.firstName}
                onChange={event =>
                  setAddUserForm(prev => ({ ...prev, firstName: event.target.value }))
                }
                placeholder="Jordan"
                autoComplete="given-name"
                disabled={addUserSubmitting}
              />
            </div>
            <div>
              <label htmlFor="add-user-last-name" className={labelClass}>
                Last name
              </label>
              <input
                id="add-user-last-name"
                type="text"
                className={inputClass}
                value={addUserForm.lastName}
                onChange={event =>
                  setAddUserForm(prev => ({ ...prev, lastName: event.target.value }))
                }
                placeholder="Rivera"
                autoComplete="family-name"
                disabled={addUserSubmitting}
              />
            </div>
          </div>
          <div>
            <label htmlFor="add-user-email" className={labelClass}>
              Email
            </label>
            <input
              id="add-user-email"
              type="email"
              className={inputClass}
              value={addUserForm.email}
              onChange={event =>
                setAddUserForm(prev => ({ ...prev, email: event.target.value }))
              }
              placeholder="jordan@example.com"
              autoComplete="email"
              disabled={addUserSubmitting}
            />
          </div>
          <div>
            <label htmlFor="add-user-password" className={labelClass}>
              Temporary password
            </label>
            <div className="relative">
              <input
                id="add-user-password"
                type={showAddUserPassword ? 'text' : 'password'}
                className={`${inputClass} pr-28`}
                value={addUserForm.password}
                onChange={event =>
                  setAddUserForm(prev => ({ ...prev, password: event.target.value }))
                }
                placeholder="At least 8 characters"
                autoComplete="new-password"
                disabled={addUserSubmitting}
              />
              <button
                type="button"
                onClick={() => setShowAddUserPassword(prev => !prev)}
                className={`${inputToggleButtonClass} ${addUserSubmitting ? 'cursor-not-allowed opacity-60' : ''}`}
                disabled={addUserSubmitting}
              >
                {showAddUserPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className={`${descriptionClass} mt-2`}>
              Share this with your teammate; they can update it after signing in.
            </p>
          </div>
          <div>
            <label htmlFor="add-user-role" className={labelClass}>
              Role
            </label>
            <select
              id="add-user-role"
              className={inputClass}
              value={addUserForm.role}
              onChange={event =>
                setAddUserForm(prev => ({
                  ...prev,
                  role: event.target.value as AddUserFormState['role'],
                }))
              }
              disabled={addUserSubmitting}
            >
              {addUserRoleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {addUserError && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                isDarkMode
                  ? 'border-red-500/40 bg-red-500/10 text-red-200'
                  : 'border-red-300 bg-red-50 text-red-600'
              }`}
            >
              {addUserError}
            </div>
          )}
        </div>
      </ActionModal>
    </DashboardLayout>
  );
}
